import React from 'react';
import { createSelector } from 'reselect';
import { RecordedDocument, RecordedDocuments } from 'pages/Search/models';
import DocumentsListItem from '../DocumentsListItem/DocumentsListItem';
import { Empty, Row, Col, Radio, Tooltip } from 'antd';
import { BasicTable, tabSelector, getDataTypeName } from 'common';
import { TableHeader } from '../';
import { useSelector, useDispatch } from 'react-redux';
import {
  searchShowReferncesUpdate,
  showReferencesSelector,
} from 'pages/Search';
import { AppState } from 'store';
import { ResultTypeName } from 'pages/Search/Search.types';

const RadioGroup = Radio.Group;

const selector = createSelector(
  showReferencesSelector,
  tabSelector,
  (state: AppState) =>
    state.search.nameData && state.search.nameData.searchCriteria,
  (getShowReferences, tabName, searchCriteria) => ({
    getShowReferences,
    tabName,
    searchCriteria,
  }),
);

const DocumentsList: React.FC<{
  documents: RecordedDocuments;
  groupName: string;
  compName?: string;
  resultType: ResultTypeName;
  resultItemIndex: number;
}> = ({ documents, groupName, compName = '', resultType, resultItemIndex }) => {
  const { getShowReferences, tabName, searchCriteria } = useSelector(selector);
  const { header, items } = documents;
  const dispatch = useDispatch();

  const showReferences = React.useMemo(() => {
    // @ts-ignore:
    return getShowReferences(
      getDataTypeName(tabName),
      // @ts-ignore:
      compName,
      resultItemIndex,
      resultType,
    );
  }, [compName, getShowReferences, resultItemIndex, tabName, resultType]);

  const showReferencesHandler = React.useCallback(
    ({ target: { value } }: any) => {
      dispatch(
        searchShowReferncesUpdate(
          getDataTypeName(tabName),
          // @ts-ignore:
          compName,
          value,
          resultItemIndex,
          resultType,
        ),
      );
    },
    [compName, dispatch, groupName, tabName],
  );

  const emptyDescription = React.useMemo(() => {
    const noDocsFound = `No Documents Found (${header})`;
    const isRichmondSearch = searchCriteria?.includes('Richmond County');

    if (!items?.length && isRichmondSearch && header) {
      switch (true) {
        case header.startsWith('FTLs:'): {
          return 'FTLs IN RICHMOND COUNTY ARE FOUND WITHIN THE JUDGMENT AND LIEN SEARCH!';
        }
        case header.startsWith('UCCs'): {
          return 'UCCs FOR RICHMOND COUNTY ARE NOT AVAILABLE!';
        }
        default:
          return noDocsFound;
      }
    }
    return noDocsFound;
  }, [header, items, searchCriteria]);

  //const isRichmondSearch = searchCriteria?.includes('Richmond County');

  return items?.length ? (
    <>
      <TableHeader
        title={header}
        items={items}
        group={groupName}
        documentType={compName}
        tabName={tabName}
        resultType={resultType}
        resultItemIndex={resultItemIndex}
      />
      <Row type="flex" justify="end" align="middle">
        <Col>
          <Tooltip title={'Show References'}>
            <Row>Show References:</Row>
            <Row>
              <RadioGroup
                value={showReferences}
                onChange={showReferencesHandler}
              >
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </RadioGroup>
            </Row>
          </Tooltip>
        </Col>
      </Row>
      <BasicTable
        items={items}
        renderRow={(text: string, record: RecordedDocument, index: number) => {
          return (
            <DocumentsListItem
              key={record.guid}
              showReferences={showReferences}
              document={record}
              index={index}
              resultType={resultType}
              resultItemIndex={resultItemIndex}
            />
          );
        }}
      />
    </>
  ) : (
    <>
      <Row type="flex" justify="center" align="middle">
        <Empty description={emptyDescription} />
      </Row>
    </>
  );
};

export default React.memo(DocumentsList);
